/**
 * AI Assistant REST HTTP Client
 *
 * Zero-dependency HTTP client for communicating with the Frost AI backend.
 * Automatically unwraps `Result<T>` responses (`{ code, message, data, traceId }`).
 * Designed for the core package with no framework dependency.
 */

import type {
  AssistantMessage,
  Conversation,
  ModelOption,
} from './protocol/types'
import type {
  ArtifactListResult,
  BoundedArtifactContent,
  PagePreviewManifest,
  PublicArtifactKind,
  PublicArtifactRef,
  PublicArtifactStatus,
} from './artifact/contracts'

interface ApiResult<T> {
  code: number
  message: string
  data: T
  traceId?: string
}

export interface PluginConfig {
  systemCode: string
  defaultMode: string
  themeStrategy: string
  themePreset: string
  primaryColor: string
  defaultModelCode: string
  enableFullscreen: boolean
  enableDrawer: boolean
  enableKnowledge: boolean
  rateLimitPerHour: number
}

export class AiHttpError extends Error {
  readonly code: number
  readonly traceId?: string

  constructor(code: number, message: string, traceId?: string) {
    super(message)
    this.name = 'AiHttpError'
    this.code = code
    this.traceId = traceId
  }
}

/** Server view of one uploaded conversation attachment. */
export interface UploadedAttachment {
  id: string
  name: string
  contentType: string
  size: number
  /** text | unsupported | failed — whether the backend extracted text. */
  extractStatus: 'extracted' | 'unsupported' | 'failed'
}

/** Attachment content preview payload. */
export interface AttachmentContent {
  id: string
  name: string
  contentType: string
  size: number
  extractStatus: string
  /** 提取文本（仅文本类；二进制为 null）。 */
  text: string | null
}

export class AiHttpClient {
  private readonly baseUrl: string
  private readonly headersProvider: () => Record<string, string>

  constructor(baseUrl: string, headersProvider: () => Record<string, string>) {
    this.baseUrl = baseUrl.replace(/\/+$/, '')
    this.headersProvider = headersProvider
  }

  async getPluginConfig(systemCode: string): Promise<PluginConfig | null> {
    const raw = await this.request<Record<string, unknown>>(
      'GET',
      `/plugin/config?systemCode=${encodeURIComponent(systemCode)}`,
    ).catch(() => null)

    if (!raw) {
      return null
    }

    return {
      systemCode: (raw.systemCode as string) ?? systemCode,
      defaultMode: (raw.defaultMode as string) ?? 'floating',
      themeStrategy: (raw.themeStrategy as string) ?? 'system',
      themePreset: (raw.themePreset as string) ?? 'light',
      primaryColor: (raw.primaryColor as string) ?? '#409EFF',
      defaultModelCode: (raw.defaultModelCode as string) ?? 'default',
      enableFullscreen: Boolean(raw.enableFullscreen ?? true),
      enableDrawer: Boolean(raw.enableDrawer ?? true),
      enableKnowledge: Boolean(raw.enableKnowledge ?? false),
      rateLimitPerHour: Number(raw.rateLimitPerHour ?? 100),
    }
  }

  async listModels(): Promise<ModelOption[]> {
    const raw = await this.request<Array<Record<string, unknown>>>('GET', '/models')

    return raw.map((item) => ({
      code: (item.code as string) ?? '',
      name: (item.name as string) ?? '',
      supportStream: Boolean(item.supportStream ?? true),
      supportToolCall: Boolean(item.supportToolCall ?? false),
      bestFor: ((item.bestFor as ModelOption['bestFor']) ?? []).filter(Boolean),
      maxTokens: typeof item.maxTokens === 'number' ? item.maxTokens : undefined,
    })).filter((item) => item.code && item.name)
  }

  async createConversation(
    systemCode: string,
    title?: string,
    modelCode?: string,
    sceneCode?: string,
  ): Promise<Conversation> {
    return this.request<Conversation>('POST', '/conversations', {
      systemCode,
      title: title ?? 'New conversation',
      modelCode: modelCode ?? 'default',
      sceneCode,
    })
  }

  async listConversations(
    systemCode: string,
    page = 1,
    size = 20,
  ): Promise<Conversation[]> {
    return this.request<Conversation[]>(
      'GET',
      `/conversations?systemCode=${encodeURIComponent(systemCode)}&page=${page}&size=${size}`,
    )
  }

  async getMessages(conversationId: string): Promise<AssistantMessage[]> {
    return this.request<AssistantMessage[]>(
      'GET',
      `/conversations/${encodeURIComponent(conversationId)}/messages`,
    )
  }

  async deleteConversation(conversationId: string): Promise<void> {
    await this.request<void>(
      'DELETE',
      `/conversations/${encodeURIComponent(conversationId)}`,
    )
  }

  /**
   * Fork a conversation into a new branch, copying history up to and including
   * `messageId` (or the full history when omitted). Returns the new conversation.
   */
  async forkConversation(conversationId: string, messageId?: string): Promise<Conversation> {
    return this.request<Conversation>(
      'POST',
      `/conversations/${encodeURIComponent(conversationId)}/fork`,
      { messageId: messageId ?? '' },
    )
  }

  /**
   * Soft-delete messages at (`from`, inclusive) or after (`after`, exclusive) an
   * anchor message. Returns the number of removed rows.
   */
  async truncateMessages(
    conversationId: string,
    messageId: string,
    mode: 'from' | 'after',
  ): Promise<number> {
    return this.request<number>(
      'DELETE',
      `/conversations/${encodeURIComponent(conversationId)}/messages`
        + `?messageId=${encodeURIComponent(messageId)}&mode=${encodeURIComponent(mode)}`,
    )
  }

  async getSuggestions(systemCode: string, sceneCode?: string): Promise<string[]> {
    let path = `/suggestions?systemCode=${encodeURIComponent(systemCode)}`
    if (sceneCode) {
      path += `&sceneCode=${encodeURIComponent(sceneCode)}`
    }
    return this.request<string[]>('GET', path)
  }

  async confirmAction(actionId: string, confirm: boolean): Promise<void> {
    await this.request<void>('POST', '/actions/confirm', {
      actionId,
      confirm,
    })
  }

  async submitFeedback(
    messageId: string,
    rating: number,
    comment?: string,
  ): Promise<void> {
    await this.request<void>('POST', '/feedback', {
      messageId,
      rating,
      comment,
    })
  }

  /** Explicitly cancel a live DSH turn without conflating it with SSE detach. */
  async cancelChat(conversationId: string, messageId: string): Promise<boolean> {
    return this.request<boolean>('POST', '/v2/chat/cancel', {
      conversationId,
      messageId,
    })
  }

  /**
   * Artifact workbench JSON APIs (06 spec §3.5). All conversation-scoped per
   * 02-contract-storage-api §5; binary bytes go through fetchArtifactBytes.
   */
  async listArtifacts(
    conversationId: string,
    opts?: { cursor?: string; kind?: PublicArtifactKind; status?: PublicArtifactStatus },
  ): Promise<ArtifactListResult> {
    const params = new URLSearchParams()
    if (opts?.cursor) params.set('cursor', opts.cursor)
    if (opts?.kind) params.set('kind', opts.kind)
    if (opts?.status) params.set('status', opts.status)
    const query = params.toString()
    const suffix = query ? `?${query}` : ''
    return this.request<ArtifactListResult>(
      'GET',
      `/conversations/${encodeURIComponent(conversationId)}/artifacts${suffix}`,
    )
  }

  async getArtifact(conversationId: string, artifactId: string): Promise<PublicArtifactRef> {
    return this.request<PublicArtifactRef>(
      'GET',
      `/conversations/${encodeURIComponent(conversationId)}/artifacts/${encodeURIComponent(artifactId)}`,
    )
  }

  async getPreviewManifest(
    conversationId: string,
    artifactId: string,
  ): Promise<PagePreviewManifest> {
    return this.request<PagePreviewManifest>(
      'GET',
      `/conversations/${encodeURIComponent(conversationId)}/artifacts/${encodeURIComponent(artifactId)}/preview`,
    )
  }

  /** Bounded content hydration for markdown/text/table viewers. */
  async getArtifactContent(
    conversationId: string,
    artifactId: string,
  ): Promise<BoundedArtifactContent> {
    return this.request<BoundedArtifactContent>(
      'GET',
      `/conversations/${encodeURIComponent(conversationId)}/artifacts/${encodeURIComponent(artifactId)}/content`,
    )
  }

  /** Preview page image URL (browser constructs it from ids per 02 §7). */
  artifactPreviewPageUrl(
    conversationId: string,
    artifactId: string,
    page: number,
    variant?: 'thumbnail' | 'full',
  ): string {
    const variantSuffix = variant && variant !== 'full' ? `?variant=${variant}` : ''
    return (
      `${this.baseUrl}/conversations/${encodeURIComponent(conversationId)}` +
      `/artifacts/${encodeURIComponent(artifactId)}` +
      `/preview/pages/${encodeURIComponent(String(page))}${variantSuffix}`
    )
  }

  /** Original-file download URL (Range-capable, attachment disposition). */
  artifactDownloadUrl(conversationId: string, artifactId: string): string {
    return (
      `${this.baseUrl}/conversations/${encodeURIComponent(conversationId)}` +
      `/artifacts/${encodeURIComponent(artifactId)}/download`
    )
  }

  /**
   * Binary fetch for preview pages and downloads. Auth headers ride the same
   * provider as every other call; failures map to AiHttpError with status so
   * the store can render 409/410/404 distinctly (02 §5).
   */
  async fetchArtifactBytes(url: string, signal?: AbortSignal): Promise<Blob> {
    const res = await fetch(url, {
      method: 'GET',
      headers: this.headersProvider(),
      signal,
    })
    if (!res.ok) {
      throw new AiHttpError(res.status, `HTTP ${res.status}: ${res.statusText}`)
    }
    return res.blob()
  }

  /**
   * Upload one conversation attachment (multipart). Runs text extraction
   * server-side; returns the metadata the turn request later references.
   * Deliberately uses raw fetch (not `request`): FormData must set its own
   * multipart boundary Content-Type.
   */
  async uploadAttachment(
    conversationId: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<UploadedAttachment> {
    const form = new FormData()
    form.append('file', file, file.name)
    const url = `${this.baseUrl}/conversations/${encodeURIComponent(conversationId)}/attachments`
    const headers = { ...this.headersProvider() }
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === 'content-type') delete headers[key]
    }
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: form,
      signal,
    })
    if (!res.ok) {
      let message = `HTTP ${res.status}: ${res.statusText}`
      let traceId: string | undefined
      try {
        const error = await res.json() as Partial<ApiResult<unknown>>
        if (typeof error.message === 'string' && error.message.trim()) message = error.message
        traceId = error.traceId
      } catch {
        // Non-JSON proxy/server errors retain the HTTP fallback above.
      }
      throw new AiHttpError(res.status, message, traceId)
    }
    const json = await res.json() as ApiResult<UploadedAttachment>
    if (json.code !== undefined && json.code !== 200) {
      throw new AiHttpError(json.code, json.message ?? `附件上传失败 (code ${json.code})`, json.traceId)
    }
    return json.data
  }

  /** 附件内容预览（文本类带提取文本；二进制类 text 为 null）。 */
  async getAttachmentContent(
    conversationId: string,
    attachmentId: string,
  ): Promise<AttachmentContent> {
    return this.request<AttachmentContent>(
      'GET',
      `/conversations/${encodeURIComponent(conversationId)}` +
        `/attachments/${encodeURIComponent(attachmentId)}/content`,
    )
  }

  async health(): Promise<{ status: string; service: string }> {
    const healthUrl = this.baseUrl.endsWith('/api/ai')
      ? `${this.baseUrl.slice(0, -'/api/ai'.length)}/actuator/health`
      : `${this.baseUrl}/health`

    const res = await fetch(healthUrl, {
      method: 'GET',
      headers: this.headersProvider(),
    })

    if (!res.ok) {
      throw new AiHttpError(res.status, `HTTP ${res.status}: ${res.statusText}`)
    }

    const json = await res.json() as ApiResult<{ status: string; service: string }> | { status: string }
    if ('code' in json) {
      if (json.code !== 200) {
        throw new AiHttpError(json.code, json.message ?? 'Unknown API error', json.traceId)
      }
      return json.data
    }

    return {
      status: json.status,
      service: 'ai-gateway',
    }
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`
    const headers = {
      'Content-Type': 'application/json',
      ...this.headersProvider(),
    }

    const init: RequestInit = {
      method,
      headers,
    }

    if (body !== undefined && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      init.body = JSON.stringify(body)
    }

    const res = await fetch(url, init)
    if (!res.ok) {
      throw new AiHttpError(res.status, `HTTP ${res.status}: ${res.statusText}`)
    }

    if (res.status === 204 || method === 'DELETE') {
      return undefined as T
    }

    const json = await res.json() as ApiResult<T>
    if (json.code !== undefined && json.code !== 200) {
      throw new AiHttpError(
        json.code,
        json.message ?? 'Unknown API error',
        json.traceId,
      )
    }

    return json.data as T
  }
}
